import mapboxgl from 'mapbox-gl'
import { RouteData, Climb } from '@cya-routes/shared'
import { ClimbDetectionService } from '../services/climbDetectionService'

interface FlybyOptions {
  speed?: number // Speed in km/minute (default 1 km/min)
  altitude?: number // Camera altitude in meters
  pitch?: number // Camera pitch angle
  bearing?: 'auto' | number // Camera bearing (auto follows route)
  showClimbs?: boolean // Show climb highlights during flyby
}

export class RouteFlyby {
  private map: mapboxgl.Map
  private animation: number | null = null
  private startTime: number = 0
  private isPaused: boolean = false
  private pausedTime: number = 0
  private onComplete?: () => void
  private onProgress?: (progress: number) => void
  private onClimbChange?: (climb: Climb | null) => void
  private tracerMarker: mapboxgl.Marker | null = null
  private currentSpeed: number = 1 // km/minute
  private animationDuration: number = 0
  private climbs: Climb[] = []
  private currentClimbIndex: number = -1
  private currentPitch: number = 60
  private currentBearingOffset: number = 0 // Offset to add to auto bearing
  private currentZoom: number = 13.5 // Default zoom level
  private routeDistances: number[] = [] // Cumulative distances for each coordinate
  private totalRouteDistance: number = 0 // Total route distance
  private lastCameraAltitude: number = 0 // For smoothing altitude changes
  private lastBearing: number = 0 // For smoothing bearing changes

  constructor(map: mapboxgl.Map) {
    this.map = map
  }

  start(
    route: RouteData, 
    options: FlybyOptions = {},
    callbacks?: {
      onComplete?: () => void
      onProgress?: (progress: number) => void
      onClimbChange?: (climb: Climb | null) => void
    }
  ) {
    console.log('🚁 FLYBY START - Beginning 3D tour')
    
    // Cancel any existing animation
    this.stop()

    const {
      speed = 1, // 1 km/minute default speed
      altitude = 1000, // 1km default altitude
      pitch = 60, // 60 degree pitch for 3D effect
      bearing = 'auto',
      showClimbs = true // Show climbs by default
    } = options
    
    // Store the initial pitch
    this.currentPitch = pitch
    
    // Calculate duration based on route distance and speed
    const routeDistance = route.stats?.distance || 0
    this.currentSpeed = speed
    this.animationDuration = (routeDistance / speed) * 60 * 1000 // Convert to milliseconds

    this.onComplete = callbacks?.onComplete
    this.onProgress = callbacks?.onProgress
    this.onClimbChange = callbacks?.onClimbChange

    if (!route.coordinates || route.coordinates.length < 2) {
      console.error('Route must have at least 2 coordinates')
      return
    }

    // Detect climbs if enabled and elevation data is available
    if (showClimbs && route.elevation?.profile && route.elevation.profile.length > 0) {
      this.climbs = ClimbDetectionService.detectClimbs(route.elevation.profile)
      console.log(`Found ${this.climbs.length} climbs for flyby highlighting`)
    } else {
      this.climbs = []
    }

    // Switch to 3D terrain if available
    console.log('🚁 FLYBY - Checking for terrain source...')
    if (this.map.getSource('mapbox-dem')) {
      console.log('🚁 FLYBY - Setting 3D terrain with exaggeration 1.5')
      this.map.setTerrain({ source: 'mapbox-dem', exaggeration: 1.5 })
      
      // Force re-render of map content after terrain change
      // Some Mapbox versions clear custom markers when terrain is set
      this.map.once('idle', () => {
        console.log('🚁 FLYBY - Map idle after terrain set, firing terrain-applied event')
        // Trigger a custom event to notify renderers to re-render
        this.map.fire('terrain-applied')
      })
    } else {
      console.log('🚁 FLYBY - No terrain source available')
    }

    // Create tracer marker and add to map at start position
    this.createTracerMarker()
    if (this.tracerMarker) {
      this.tracerMarker.setLngLat(route.coordinates[0]).addTo(this.map)
    }

    // Pre-calculate cumulative distances for performance
    this.routeDistances = this.preCalculateDistances(route.coordinates)
    this.totalRouteDistance = this.routeDistances[this.routeDistances.length - 1] || 0
    
    this.startTime = performance.now()
    
    // Track marker count periodically
    let frameCount = 0
    const checkMarkersEvery = 60 // Check every 60 frames (approximately once per second)

    const animate = () => {
      if (this.isPaused) {
        this.animation = requestAnimationFrame(animate)
        return
      }
      
      // Check markers periodically
      frameCount++
      if (frameCount % checkMarkersEvery === 0) {
        const markerCount = this.map._poiMarkers ? this.map._poiMarkers.size : 0
        console.log(`🚁 FLYBY FRAME ${frameCount} - POI marker count: ${markerCount}`)
      }

      const currentTime = performance.now()
      const elapsed = currentTime - this.startTime - this.pausedTime
      const progress = Math.min(elapsed / this.animationDuration, 1)

      // Calculate target distance based on time progress
      const targetDistance = progress * this.totalRouteDistance
      
      // Find position on original route based on distance
      const position = this.getPositionAtDistance(targetDistance, route.coordinates)
      
      // For camera, use the position directly for smooth movement
      const lng = position.lng
      const lat = position.lat

      // Calculate bearing if auto - look ahead a fixed distance
      let targetBearing = typeof bearing === 'number' ? bearing : 0
      if (bearing === 'auto' && targetDistance < this.totalRouteDistance - 0.5) {
        // Look ahead 500m for smooth bearing
        const lookAheadDist = Math.min(0.5, this.totalRouteDistance - targetDistance)
        const lookAheadPosition = this.getPositionAtDistance(targetDistance + lookAheadDist, route.coordinates)
        targetBearing = this.calculateBearing(lng, lat, lookAheadPosition.lng, lookAheadPosition.lat)
      }
      
      // Smooth bearing changes to slow down rotation
      let smoothedBearing = targetBearing
      if (this.lastBearing !== 0 || progress > 0.01) {
        // Initialize bearing on first meaningful frame
        if (this.lastBearing === 0) {
          this.lastBearing = targetBearing
        }
        
        // Calculate bearing difference, handling wrap-around
        let bearingDiff = targetBearing - this.lastBearing
        
        // Normalize to -180 to 180 range
        if (bearingDiff > 180) bearingDiff -= 360
        if (bearingDiff < -180) bearingDiff += 360
        
        // Apply very heavy smoothing - only turn 3% of the difference per frame
        const turnRate = 0.03
        smoothedBearing = this.lastBearing + bearingDiff * turnRate
        
        // Normalize the result
        smoothedBearing = (smoothedBearing + 360) % 360
        this.lastBearing = smoothedBearing
      }

      // Get elevation for more realistic altitude
      let cameraAltitude = altitude
      if (route.elevation?.profile && route.elevation.profile.length > 0) {
        // Find elevation at current distance
        const routeDistance = route.stats?.distance || 0
        const currentDistance = progress * routeDistance
        
        // Look ahead further and use weighted average for smoother elevation
        const lookAheadDistance = 1.0 // Look 1km ahead for better anticipation
        const lookBehindDistance = 0.3 // Look 300m behind for smoother transitions
        
        const aheadDistance = Math.min(currentDistance + lookAheadDistance, routeDistance)
        const behindDistance = Math.max(currentDistance - lookBehindDistance, 0)
        
        // Get elevations at multiple points for better smoothing
        const currentElevation = this.getElevationAtDistance(currentDistance, route.elevation.profile)
        const aheadElevation = this.getElevationAtDistance(aheadDistance, route.elevation.profile)
        const behindElevation = this.getElevationAtDistance(behindDistance, route.elevation.profile)
        
        // Also sample intermediate points for even smoother transitions
        const midAheadDistance = Math.min(currentDistance + lookAheadDistance * 0.5, routeDistance)
        const midAheadElevation = this.getElevationAtDistance(midAheadDistance, route.elevation.profile)
        
        // Weighted average with more points for smoother result
        const smoothedElevation = (
          behindElevation * 0.15 + 
          currentElevation * 0.25 + 
          midAheadElevation * 0.30 +
          aheadElevation * 0.30
        )
        
        // Calculate target altitude to maintain consistent height above terrain
        // Add a base smoothing to the terrain elevation itself
        const terrainSmoothing = 0.7 // Only follow 70% of terrain changes
        const baseElevation = this.lastCameraAltitude > 0 
          ? (this.lastCameraAltitude - altitude) * (1 - terrainSmoothing) + smoothedElevation * terrainSmoothing
          : smoothedElevation
        
        const targetAltitude = baseElevation + altitude
        
        // Apply heavy smoothing for camera altitude
        if (this.lastCameraAltitude === 0) {
          // First frame - set initial altitude
          cameraAltitude = targetAltitude
          this.lastCameraAltitude = targetAltitude
        } else {
          // Much lower smoothing factor for gentler transitions
          const smoothingFactor = 0.05 // Only 5% change per frame
          cameraAltitude = this.lastCameraAltitude + (targetAltitude - this.lastCameraAltitude) * smoothingFactor
          
          // Add velocity dampening for steep changes
          const altitudeChange = targetAltitude - this.lastCameraAltitude
          const maxChangePerFrame = 5 // Max 5 meters altitude change per frame
          if (Math.abs(altitudeChange) > maxChangePerFrame) {
            const clampedChange = Math.sign(altitudeChange) * maxChangePerFrame
            cameraAltitude = this.lastCameraAltitude + clampedChange * smoothingFactor
          }
          
          this.lastCameraAltitude = cameraAltitude
        }
      }

      // Apply bearing offset if using auto bearing
      const finalBearing = bearing === 'auto' ? smoothedBearing + this.currentBearingOffset : smoothedBearing
      
      // Update camera position with easing for smoother movement
      this.map.easeTo({
        center: [lng, lat],
        zoom: this.currentZoom,
        pitch: this.currentPitch,
        bearing: finalBearing,
        duration: 100, // Small easing duration for smoothness
        easing: (t) => t // Linear easing
      })

      // Update tracer marker position
      if (this.tracerMarker) {
        // Use the same position as the camera for consistency
        this.tracerMarker.setLngLat([lng, lat])
      }

      // Check if we're in a climb and update highlighting
      if (this.climbs.length > 0) {
        // Use the actual distance we've calculated
        
        // Find which climb we're in (if any)
        let inClimb: Climb | null = null
        let climbIndex = -1
        
        for (let i = 0; i < this.climbs.length; i++) {
          const climb = this.climbs[i]
          if (targetDistance >= climb.startDistance && targetDistance <= climb.endDistance) {
            inClimb = climb
            climbIndex = i
            break
          }
        }
        
        // If climb state has changed, notify callback
        if (climbIndex !== this.currentClimbIndex) {
          this.currentClimbIndex = climbIndex
          if (this.onClimbChange) {
            this.onClimbChange(inClimb)
          }
        }
      }

      // Call progress callback
      if (this.onProgress) {
        this.onProgress(progress)
      }

      if (progress < 1) {
        this.animation = requestAnimationFrame(animate)
      } else {
        this.complete()
      }
    }

    // Start animation
    this.animation = requestAnimationFrame(animate)
  }

  private smoothRoute(coordinates: [number, number][], sampleRate: number = 20): [number, number][] {
    if (coordinates.length <= sampleRate * 2) {
      return coordinates // Too short to smooth
    }

    const smoothed: [number, number][] = []
    
    // Always include the first point
    smoothed.push(coordinates[0])
    
    // Sample points along the route
    for (let i = sampleRate; i < coordinates.length - sampleRate; i += sampleRate) {
      smoothed.push(coordinates[i])
    }
    
    // Always include the last point
    smoothed.push(coordinates[coordinates.length - 1])
    
    return smoothed
  }

  pause() {
    if (!this.isPaused && this.animation !== null) {
      this.isPaused = true
      // Store the elapsed time when pausing
      const currentTime = performance.now()
      const elapsed = currentTime - this.startTime - this.pausedTime
      this.pausedTime = elapsed
    }
  }

  resume() {
    if (this.isPaused && this.animation !== null) {
      this.isPaused = false
      // Reset start time but keep the paused time to continue from where we left off
      this.startTime = performance.now() - this.pausedTime
      this.pausedTime = 0
    }
  }

  stop() {
    if (this.animation !== null) {
      cancelAnimationFrame(this.animation)
      this.animation = null
      this.isPaused = false
      this.pausedTime = 0
      this.currentClimbIndex = -1
      this.lastCameraAltitude = 0 // Reset altitude smoothing
      this.lastBearing = 0 // Reset bearing smoothing
      
      // Remove tracer marker
      if (this.tracerMarker) {
        this.tracerMarker.remove()
        this.tracerMarker = null
      }
      
      // Clear any climb highlighting
      if (this.onClimbChange) {
        this.onClimbChange(null)
      }
      
      // Don't immediately change pitch here - let the calling code handle the transition
      // The useFlybyControls hook will handle zooming back to route bounds with proper animation
    }
  }

  complete() {
    this.stop()
    if (this.onComplete) {
      this.onComplete()
    }
  }

  isPlaying(): boolean {
    return this.animation !== null && !this.isPaused
  }

  isPausedState(): boolean {
    return this.animation !== null && this.isPaused
  }

  setSpeed(newSpeed: number) {
    if (this.animation === null) return
    
    // Calculate current progress
    const currentTime = performance.now()
    const elapsed = currentTime - this.startTime - this.pausedTime
    const currentProgress = Math.min(elapsed / this.animationDuration, 1)
    
    // Update speed and recalculate duration for remaining distance
    const oldSpeed = this.currentSpeed
    this.currentSpeed = newSpeed
    
    // Calculate new total duration based on new speed
    const totalDistance = (this.animationDuration * oldSpeed) / (60 * 1000) // Total route distance in km
    const newTotalDuration = (totalDistance / newSpeed) * 60 * 1000
    
    // Calculate how much time should have elapsed at current progress with new speed
    const newElapsedTime = currentProgress * newTotalDuration
    
    // Adjust start time to maintain current progress with new speed
    this.startTime = currentTime - newElapsedTime + this.pausedTime
    this.animationDuration = newTotalDuration
  }

  getCurrentSpeed(): number {
    return this.currentSpeed
  }

  private createTracerMarker() {
    // Remove existing marker if any
    if (this.tracerMarker) {
      this.tracerMarker.remove()
    }

    // Create marker element matching the tracer marker style
    const el = document.createElement('div')
    el.style.width = '13px'
    el.style.height = '13px'
    el.style.borderRadius = '50%'
    el.style.background = 'radial-gradient(circle at 30% 30%, #60a5fa, #3b82f6 40%, #2563eb 100%)'
    el.style.border = '1px solid #ffffff'
    el.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4)'
    el.style.cursor = 'pointer'
    el.style.pointerEvents = 'none'
    el.style.zIndex = '1000'

    // Create the marker
    this.tracerMarker = new mapboxgl.Marker({
      element: el,
      anchor: 'center'
    })
  }

  private calculateBearing(lng1: number, lat1: number, lng2: number, lat2: number): number {
    const dLon = (lng2 - lng1) * Math.PI / 180
    const lat1Rad = lat1 * Math.PI / 180
    const lat2Rad = lat2 * Math.PI / 180
    
    const y = Math.sin(dLon) * Math.cos(lat2Rad)
    const x = Math.cos(lat1Rad) * Math.sin(lat2Rad) -
              Math.sin(lat1Rad) * Math.cos(lat2Rad) * Math.cos(dLon)
    
    const bearing = Math.atan2(y, x) * 180 / Math.PI
    return (bearing + 360) % 360
  }

  private calculateDistance(lng1: number, lat1: number, lng2: number, lat2: number): number {
    const R = 6371 // Earth's radius in kilometers
    const dLat = (lat2 - lat1) * Math.PI / 180
    const dLon = (lng2 - lng1) * Math.PI / 180
    const lat1Rad = lat1 * Math.PI / 180
    const lat2Rad = lat2 * Math.PI / 180

    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1Rad) * Math.cos(lat2Rad) *
              Math.sin(dLon/2) * Math.sin(dLon/2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a))
    
    return R * c // Distance in kilometers
  }
  
  // Adjust bearing by a relative amount
  adjustBearing(delta: number) {
    this.currentBearingOffset += delta
  }
  
  // Adjust pitch by a relative amount
  adjustPitch(delta: number) {
    this.currentPitch = Math.max(0, Math.min(85, this.currentPitch + delta))
  }
  
  // Adjust zoom by a relative amount
  adjustZoom(delta: number) {
    this.currentZoom = Math.max(10, Math.min(18, this.currentZoom + delta))
  }

  // Pre-calculate cumulative distances for all coordinates
  private preCalculateDistances(coordinates: [number, number][]): number[] {
    const distances: number[] = [0]
    let cumulativeDistance = 0
    
    for (let i = 1; i < coordinates.length; i++) {
      const [lng1, lat1] = coordinates[i - 1]
      const [lng2, lat2] = coordinates[i]
      const segmentDistance = this.calculateDistance(lng1, lat1, lng2, lat2)
      cumulativeDistance += segmentDistance
      distances.push(cumulativeDistance)
    }
    
    return distances
  }

  // Get position at a specific distance along the route
  private getPositionAtDistance(targetDistance: number, coordinates: [number, number][]): {
    lng: number
    lat: number
    index: number
    progress: number
  } {
    // Binary search for efficiency
    let left = 0
    let right = this.routeDistances.length - 1
    
    while (left < right - 1) {
      const mid = Math.floor((left + right) / 2)
      if (this.routeDistances[mid] <= targetDistance) {
        left = mid
      } else {
        right = mid
      }
    }
    
    const index1 = left
    const index2 = Math.min(index1 + 1, coordinates.length - 1)
    
    // Interpolate between points
    const dist1 = this.routeDistances[index1]
    const dist2 = this.routeDistances[index2]
    const segmentLength = dist2 - dist1
    
    let t = 0
    if (segmentLength > 0) {
      t = (targetDistance - dist1) / segmentLength
    }
    
    const [lng1, lat1] = coordinates[index1]
    const [lng2, lat2] = coordinates[index2]
    
    return {
      lng: lng1 + (lng2 - lng1) * t,
      lat: lat1 + (lat2 - lat1) * t,
      index: index1,
      progress: index1 / (coordinates.length - 1)
    }
  }

  // Get elevation at a specific distance
  private getElevationAtDistance(distance: number, elevationProfile: Array<{ distance: number; elevation: number }>): number {
    // Binary search for efficiency
    let left = 0
    let right = elevationProfile.length - 1
    
    while (left < right - 1) {
      const mid = Math.floor((left + right) / 2)
      if (elevationProfile[mid].distance <= distance) {
        left = mid
      } else {
        right = mid
      }
    }
    
    const p1 = elevationProfile[left]
    const p2 = elevationProfile[Math.min(left + 1, elevationProfile.length - 1)]
    
    if (p1.distance === p2.distance) {
      return p1.elevation
    }
    
    const ratio = (distance - p1.distance) / (p2.distance - p1.distance)
    return p1.elevation + (p2.elevation - p1.elevation) * ratio
  }
}